/**
 * FormulaConfigPage
 * 方程式設定主檔管理（組織級，PDD 與方法論共用）
 * func_code = formula_config, system_functions.id = 118
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import 'katex/dist/katex.min.css';
import { BlockMath, InlineMath } from 'react-katex';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useToast } from '../contexts/ToastContext';
import { getSystemCodesByType, SystemCode } from '../services/systemCodeService';
import formulaConfigService, { FormulaConfigItem, FormulaParameter } from '../services/formulaConfigService';
import FunctionPageHeader from '../components/FunctionPageHeader';
import LatexHelper from '../components/LatexHelper';
import { evaluateLatex, parseLatexSymbols } from '../utils/latexEvaluator';
import { logView, logCreate, logUpdate, logDelete } from '../utils/userLogHelper';
import '../styles/DataTable.css';

// ── 常用單位快選 ──────────────────────────────────────────────────
const COMMON_UNITS = [
  { label: 'tCO₂e',     latex: '\\text{tCO}_2\\text{e}' },
  { label: 'MWh',       latex: '\\text{MWh}' },
  { label: 'GWh',       latex: '\\text{GWh}' },
  { label: 'kWh',       latex: '\\text{kWh}' },
  { label: 'GJ',        latex: '\\text{GJ}' },
  { label: 'MJ',        latex: '\\text{MJ}' },
  { label: 'kg',        latex: '\\text{kg}' },
  { label: 't',         latex: '\\text{t}' },
  { label: 'm³',        latex: '\\text{m}^3' },
  { label: 'tCO₂e/MWh', latex: '\\text{tCO}_2\\text{e/MWh}' },
  { label: 'tCO₂e/GJ',  latex: '\\text{tCO}_2\\text{e/GJ}' },
];

const EMPTY_PARAM = (): FormulaParameter => ({
  symbol: '', name_zh: '', name_en: '', unit: '', description_zh: '', description_en: '',
});

const EMPTY_FORM = (): FormulaConfigItem => ({
  id: 0, formula_code: '',
  name_zh: '', name_en: '',
  ghg_types: [], formula_latex: '',
  parameters: [], description_zh: '', description_en: '',
  is_active: true, edit_by: 0, created_at: '', updated_at: null,
});

// ── KaTeX 安全渲染 ────────────────────────────────────────────────
const SafeBlockMath: React.FC<{ math: string }> = ({ math }) => {
  try { return <BlockMath math={math} />; } catch { return <span style={{ color: '#c33', fontSize: 12 }}>LaTeX Error</span>; }
};
const SafeInlineMath: React.FC<{ math: string }> = ({ math }) => {
  try { return <InlineMath math={math} />; } catch { return <span style={{ color: '#c33', fontSize: 11 }}>{math}</span>; }
};

// ── 主元件 ────────────────────────────────────────────────────────
const FormulaConfigPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { hasPermission, loading: permissionLoading } = usePermission();
  const toast = useToast();
  const hasInitialized = useRef(false);

  const [items, setItems] = useState<FormulaConfigItem[]>([]);
  const [ghgCodes, setGhgCodes] = useState<SystemCode[]>([]);
  const [loading, setLoading] = useState(false);

  // 過濾條件
  const [filterActive, setFilterActive] = useState<boolean | ''>('');
  const [filterKeyword, setFilterKeyword] = useState('');

  // 編輯 Modal
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<FormulaConfigItem>(EMPTY_FORM());
  const latexRef = useRef<HTMLTextAreaElement | null>(null);

  // 試算 Modal
  const [showTrial, setShowTrial] = useState(false);
  const [trialFormula, setTrialFormula] = useState<FormulaConfigItem | null>(null);
  const [trialValues, setTrialValues] = useState<Record<string, string>>({});
  const [trialResult, setTrialResult] = useState<number | null | 'error' | 'pending'>('pending');

  useEffect(() => {
    if (!hasInitialized.current && !permissionLoading) {
      validateSession('FormulaConfigPage');
      hasInitialized.current = true;
    }
  }, [permissionLoading]);

  useEffect(() => {
    getSystemCodesByType('GHG')
      .then(setGhgCodes)
      .catch(() => {});
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    const filters = { is_active: filterActive !== '' ? filterActive : undefined, keyword: filterKeyword || undefined };
    try {
      const data = await formulaConfigService.list(filters);
      setItems(data);
      logView('formula_config', filters).catch(() => {});
    } catch {
      toast.error(isZh ? '載入資料失敗' : 'Failed to load data');
      logView('formula_config', filters, '載入資料失敗').catch(() => {});
    } finally {
      setLoading(false);
    }
  }, [filterActive, filterKeyword, isZh, toast]);

  useEffect(() => { loadItems(); }, [loadItems]);

  // ── 編輯 Modal ──────────────────────────────────────────────────
  const openCreate = () => {
    setForm(EMPTY_FORM());
    setIsEditing(false);
    setShowModal(true);
  };

  const openEdit = (item: FormulaConfigItem) => {
    setForm({ ...item, parameters: item.parameters.map(p => ({ ...p })) });
    setIsEditing(true);
    setShowModal(true);
  };

  const setFormField = (field: keyof FormulaConfigItem, value: unknown) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const insertLatex = (snippet: string) => {
    const ref = latexRef.current;
    const current = form.formula_latex || '';
    if (!ref) { setFormField('formula_latex', current + snippet); return; }
    const s = ref.selectionStart ?? current.length;
    const e = ref.selectionEnd   ?? current.length;
    const next = current.slice(0, s) + snippet + current.slice(e);
    setFormField('formula_latex', next);
    setTimeout(() => {
      ref.focus();
      ref.setSelectionRange(s + snippet.length, s + snippet.length);
    }, 10);
  };

  // 解析公式參數
  const parseParams = () => {
    const latex = form.formula_latex || '';
    const symbols = parseLatexSymbols(latex);
    const existingMap = new Map(form.parameters.map(p => [p.symbol, p]));
    const merged = symbols.map(sym => existingMap.get(sym) ?? { ...EMPTY_PARAM(), symbol: sym });
    // 保留已有但不在解析結果裡的參數
    form.parameters.forEach(p => {
      if (!symbols.includes(p.symbol)) merged.push(p);
    });
    setFormField('parameters', merged);
  };

  // 參數列操作（functional updater 防 stale closure）
  const addParam = () => setForm(prev => ({ ...prev, parameters: [...prev.parameters, EMPTY_PARAM()] }));

  const updateParam = (idx: number, patch: Partial<FormulaParameter>) =>
    setForm(prev => {
      if (idx < 0 || idx >= prev.parameters.length) return prev;
      const ps = [...prev.parameters];
      ps[idx] = { ...ps[idx], ...patch };
      return { ...prev, parameters: ps };
    });

  const removeParam = (idx: number) =>
    setForm(prev => ({ ...prev, parameters: prev.parameters.filter((_, i) => i !== idx) }));

  const insertParamSymbol = (idx: number, snippet: string) =>
    setForm(prev => {
      if (idx < 0 || idx >= prev.parameters.length) return prev;
      const ps = [...prev.parameters];
      ps[idx] = { ...ps[idx], symbol: (ps[idx].symbol || '') + snippet };
      return { ...prev, parameters: ps };
    });

  const insertParamUnit = (idx: number, snippet: string) =>
    setForm(prev => {
      if (idx < 0 || idx >= prev.parameters.length) return prev;
      const ps = [...prev.parameters];
      ps[idx] = { ...ps[idx], unit: (ps[idx].unit || '') + snippet };
      return { ...prev, parameters: ps };
    });

  const handleSave = async () => {
    if (!form.formula_code.trim()) {
      toast.warning(isZh ? '請輸入公式代碼' : 'Please enter formula code');
      return;
    }
    if (!form.name_zh.trim() || !form.name_en.trim()) {
      toast.warning(isZh ? '請輸入中英文名稱' : 'Please enter names in both languages');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        formula_code:  form.formula_code.trim(),
        name_zh:       form.name_zh.trim(),
        name_en:       form.name_en.trim(),
        ghg_types:     form.ghg_types || [],
        formula_latex: form.formula_latex || '',
        parameters:    form.parameters,
        description_zh: form.description_zh || '',
        description_en: form.description_en || '',
        is_active:     form.is_active,
      };
      if (isEditing) {
        const oldData = items.find(i => i.id === form.id);
        const updated = await formulaConfigService.update(form.id, payload);
        logUpdate('formula_config', oldData ?? {}, updated).catch(() => {});
      } else {
        const created = await formulaConfigService.create(payload);
        logCreate('formula_config', created).catch(() => {});
      }
      toast.success(isZh ? '儲存成功' : 'Saved successfully');
      setShowModal(false);
      loadItems();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (detail === 'formula_code already exists') {
        toast.error(isZh ? '公式代碼已存在，請使用不同代碼' : 'Formula code already exists');
      } else {
        toast.error(detail || (isZh ? '儲存失敗' : 'Save failed'));
      }
      logCreate('formula_config', form as any, detail || '儲存失敗').catch(() => {});
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (item: FormulaConfigItem) => {
    const ok = await toast.confirm({
      title:       isZh ? '刪除確認' : 'Delete Confirmation',
      message:     isZh ? `確定要刪除「${item.name_zh}」嗎？` : `Delete "${item.name_en}"?`,
      confirmText: isZh ? '刪除' : 'Delete',
      severity:    'error',
    });
    if (!ok) return;
    try {
      await formulaConfigService.remove(item.id);
      logDelete('formula_config', item).catch(() => {});
      toast.success(isZh ? '刪除成功' : 'Deleted');
      loadItems();
    } catch (err: any) {
      const detail = err.response?.data?.detail || '刪除失敗';
      logDelete('formula_config', item, detail).catch(() => {});
      toast.error(isZh ? '刪除失敗' : 'Delete failed');
    }
  };

  // ── 試算 Modal ──────────────────────────────────────────────────

  // 從 LaTeX 公式中取得等號左邊的符號（去除空白）
  const getLhsSymbol = (latex: string): string => {
    const eqIdx = latex.search(/(?<!\\)=/);
    if (eqIdx < 0) return '';
    return latex.slice(0, eqIdx).trim();
  };

  const openTrial = (item: FormulaConfigItem) => {
    setTrialFormula(item);
    setTrialValues({});
    setTrialResult('pending');
    setShowTrial(true);
  };

  const handleCalculate = () => {
    if (!trialFormula) return;
    const lhsSym = getLhsSymbol(trialFormula.formula_latex || '');
    const params = trialFormula.parameters
      .filter(p => p.symbol !== lhsSym)
      .map(p => ({
        symbol:      p.symbol,
        trial_value: trialValues[p.symbol] !== undefined && trialValues[p.symbol] !== ''
          ? parseFloat(trialValues[p.symbol])
          : null,
      }));
    const result = evaluateLatex(trialFormula.formula_latex || '', params);
    setTrialResult(result === null ? 'error' : result);
  };

  const getGhgLabel = (id: number) => {
    const c = ghgCodes.find(c => c.id === id);
    if (!c) return String(id);
    return isZh ? c.code_cname : (c.code_ename || c.code);
  };

  // ── 渲染 ─────────────────────────────────────────────────────────
  if (permissionLoading) return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  if (!hasPermission('formula_config', 'read')) return <div className="page-container"><div className="error-message">{t('common.noPermission')}</div></div>;
  const canUpdate = hasPermission('formula_config', 'update');
  const canCreate = hasPermission('formula_config', 'create');
  const canDelete = hasPermission('formula_config', 'delete');

  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="formula_config" />
      </div>

      {/* 過濾列 */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        <select
          value={filterActive === '' ? '' : filterActive ? 'true' : 'false'}
          onChange={e => setFilterActive(e.target.value === '' ? '' : e.target.value === 'true')}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d9d9d9', fontSize: 13 }}
        >
          <option value="">{isZh ? '全部狀態' : 'All Status'}</option>
          <option value="true">{isZh ? '啟用' : 'Active'}</option>
          <option value="false">{isZh ? '停用' : 'Inactive'}</option>
        </select>

        <input
          value={filterKeyword}
          onChange={e => setFilterKeyword(e.target.value)}
          placeholder={isZh ? '搜尋代碼或名稱...' : 'Search code or name...'}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d9d9d9', fontSize: 13, minWidth: 200 }}
          onKeyDown={e => e.key === 'Enter' && loadItems()}
        />
        <button className="btn-secondary" onClick={loadItems} style={{ fontSize: 13, padding: '6px 14px' }}>
          {isZh ? '搜尋' : 'Search'}
        </button>

        {canCreate && (
          <button className="btn-primary" onClick={openCreate} style={{ marginLeft: 'auto', fontSize: 13, padding: '6px 18px' }}>
            {t('common.create')}
          </button>
        )}
      </div>

      {/* DataTable */}
      {loading ? (
        <div className="loading">{t('common.loading')}</div>
      ) : items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#999', fontSize: 15 }}>
          {isZh ? '尚無公式，請點擊「新增」建立第一個公式' : 'No formulas yet. Click "Create" to add one.'}
        </div>
      ) : (
        <div className="data-table-container">
          <table className="data-table">
            <thead className="table-header-dark-green">
              <tr>
                <th style={{ width: '4%' }}>#</th>
                <th style={{ width: '10%' }}>{isZh ? '代碼' : 'Code'}</th>
                <th style={{ width: '13%' }}>{isZh ? '名稱' : 'Name'}</th>
                <th style={{ width: '14%' }}>{isZh ? '適用 GHG' : 'GHG Types'}</th>
                <th style={{ width: '28%' }}>{isZh ? '公式' : 'Formula'}</th>
                <th style={{ width: '6%' }}>{isZh ? '參數' : 'Params'}</th>
                <th style={{ width: '5%' }}>{isZh ? '啟用' : 'Active'}</th>
                <th style={{ width: '20%' }}>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={item.id}>
                  <td>{idx + 1}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{item.formula_code}</td>
                  <td>{isZh ? item.name_zh : item.name_en}</td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                      {item.ghg_types && item.ghg_types.length > 0
                        ? item.ghg_types.map(id => (
                            <span key={id} style={{ fontSize: 10, padding: '1px 6px', background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: 10, color: '#0050b3', whiteSpace: 'nowrap' }}>
                              {getGhgLabel(id)}
                            </span>
                          ))
                        : <span style={{ color: '#bbb', fontSize: 12 }}>—</span>
                      }
                    </div>
                  </td>
                  <td style={{ maxWidth: 0, overflow: 'hidden' }}>
                    {item.formula_latex
                      ? <SafeBlockMath math={item.formula_latex} />
                      : <span style={{ color: '#bbb' }}>—</span>
                    }
                  </td>
                  <td style={{ textAlign: 'center' }}>{item.parameters?.length ?? 0}</td>
                  <td style={{ textAlign: 'center' }}>{item.is_active ? '✅' : '❌'}</td>
                  <td className="actions" style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn-secondary" onClick={() => openTrial(item)}
                      style={{ fontSize: 12, padding: '3px 10px', marginRight: 6 }}>
                      {isZh ? '試算' : 'Trial'}
                    </button>
                    {canUpdate && (
                      <button className="btn-edit" onClick={() => openEdit(item)} style={{ marginRight: 6 }}>
                        {t('common.edit')}
                      </button>
                    )}
                    {canDelete && (
                      <button className="btn-delete" onClick={() => handleDelete(item)}>
                        {t('common.delete')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── 編輯 Modal ────────────────────────────────────────────── */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}
            style={{ maxWidth: 900, width: '95vw' }}>
            <div className="modal-header">
              <h2>{isEditing ? (isZh ? '編輯公式' : 'Edit Formula') : (isZh ? '新增公式' : 'Add Formula')}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>

            <div className="modal-body" style={{ maxHeight: '75vh', overflowY: 'auto', padding: '0 4px' }}>

              {/* 區塊一：基本資訊 */}
              <div style={{ background: '#fafafa', borderRadius: 8, padding: '16px 20px', marginBottom: 16, border: '1px solid #eee' }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#333', marginBottom: 12 }}>
                  {isZh ? '基本資訊' : 'Basic Information'}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={labelStyle}>{isZh ? '公式代碼 *' : 'Formula Code *'}</label>
                    <input value={form.formula_code} onChange={e => setFormField('formula_code', e.target.value.toUpperCase())}
                      placeholder="BE_BASELINE"
                      style={{ ...inputStyle, fontFamily: 'monospace' }} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 20 }}>
                    <label style={{ fontSize: 13, fontWeight: 600 }}>{isZh ? '啟用' : 'Active'}</label>
                    <input type="checkbox" checked={form.is_active} onChange={e => setFormField('is_active', e.target.checked)} />
                  </div>
                  <div>
                    <label style={labelStyle}>{isZh ? '中文名稱 *' : 'Chinese Name *'}</label>
                    <input value={form.name_zh} onChange={e => setFormField('name_zh', e.target.value)}
                      style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>{isZh ? '英文名稱 *' : 'English Name *'}</label>
                    <input value={form.name_en} onChange={e => setFormField('name_en', e.target.value)}
                      style={inputStyle} />
                  </div>
                </div>

                {/* GHG 多選 */}
                <div style={{ marginTop: 14 }}>
                  <label style={labelStyle}>{isZh ? '適用溫室氣體（可多選）' : 'Applicable GHG Types (multi-select)'}</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 6 }}>
                    {ghgCodes.map(c => {
                      const checked = (form.ghg_types || []).includes(c.id);
                      return (
                        <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                          padding: '4px 10px', borderRadius: 20, fontSize: 13,
                          background: checked ? '#e6f7ff' : '#fafafa',
                          border: `1px solid ${checked ? '#1890ff' : '#d9d9d9'}`,
                          color: checked ? '#0050b3' : '#555',
                          transition: 'all 0.15s',
                        }}>
                          <input type="checkbox" checked={checked}
                            onChange={e => {
                              const cur = form.ghg_types || [];
                              setFormField('ghg_types', e.target.checked
                                ? [...cur, c.id]
                                : cur.filter(id => id !== c.id));
                            }}
                            style={{ display: 'none' }}
                          />
                          {checked ? '✓ ' : ''}{isZh ? c.code_cname : (c.code_ename || c.code)}
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* 區塊二：公式輸入 */}
              <div style={{ background: '#fafafa', borderRadius: 8, padding: '16px 20px', marginBottom: 16, border: '1px solid #eee' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#333' }}>
                    {isZh ? 'LaTeX 公式' : 'Formula (LaTeX)'}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button type="button" className="btn-secondary"
                      onClick={parseParams}
                      style={{ fontSize: 12, padding: '4px 12px' }}>
                      {isZh ? '▶ 解析參數' : '▶ Parse Params'}
                    </button>
                    <LatexHelper isZh={isZh} onInsert={insertLatex} />
                  </div>
                </div>

                <textarea
                  ref={latexRef}
                  value={form.formula_latex || ''}
                  onChange={e => setFormField('formula_latex', e.target.value)}
                  rows={3}
                  placeholder={isZh ? '輸入 LaTeX 語法，例如：BE_{y} = \\sum_{i}(AD_{i,y} \\times EF_{i,y})' : 'Enter LaTeX, e.g.: BE_{y} = \\sum_{i}(AD_{i,y} \\times EF_{i,y})'}
                  style={{ width: '100%', fontFamily: 'monospace', fontSize: 13, padding: '8px 10px', borderRadius: 6, border: '1px solid #d9d9d9', resize: 'vertical', boxSizing: 'border-box' }}
                />

                {form.formula_latex && (
                  <div style={{ marginTop: 8, padding: '10px 16px', background: '#fff', borderRadius: 6, border: '1px solid #e8e8e8', overflowX: 'auto' }}>
                    <SafeBlockMath math={form.formula_latex} />
                  </div>
                )}
              </div>

              {/* 區塊三：參數定義 */}
              <div style={{ background: '#fafafa', borderRadius: 8, padding: '16px 20px', marginBottom: 16, border: '1px solid #eee' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#333' }}>
                    {isZh ? '參數定義' : 'Parameter Definitions'}
                  </div>
                  <button type="button" className="btn-primary" onClick={addParam}
                    style={{ fontSize: 12, padding: '4px 12px' }}>
                    {isZh ? '+ 新增參數' : '+ Add Parameter'}
                  </button>
                </div>

                {form.parameters.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#bbb', fontSize: 13, padding: '16px 0' }}>
                    {isZh ? '尚無參數，可點擊「解析參數」自動從公式提取，或手動新增' : 'No parameters. Use "Parse Params" or add manually.'}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {form.parameters.map((p, idx) => (
                      <div key={idx} style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: '12px 14px', background: '#fff', position: 'relative' }}>
                        {/* 刪除按鈕 */}
                        <button type="button" onClick={() => removeParam(idx)}
                          style={{ position: 'absolute', top: 10, right: 10, background: 'none', border: 'none', color: '#c33', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>✕</button>

                        {/* 第一行：符號 + 單位 */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 8 }}>
                          {/* 符號 */}
                          <div>
                            <label style={{ ...paramLabelStyle }}>{isZh ? '符號' : 'Symbol'} *</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                              <input value={p.symbol} onChange={e => updateParam(idx, { symbol: e.target.value })}
                                style={{ ...cellInput, flex: 1, fontFamily: 'monospace', fontSize: 13 }} />
                              <LatexHelper isZh={isZh} onInsert={s => insertParamSymbol(idx, s)} />
                            </div>
                            {p.symbol && (
                              <div style={{ fontSize: 14, color: '#444', minHeight: 20 }}>
                                <SafeInlineMath math={p.symbol} />
                              </div>
                            )}
                          </div>
                          {/* 單位 */}
                          <div>
                            <label style={{ ...paramLabelStyle }}>{isZh ? '單位' : 'Unit'}</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                              <input value={p.unit || ''} onChange={e => updateParam(idx, { unit: e.target.value })}
                                style={{ ...cellInput, flex: 1, fontFamily: 'monospace', fontSize: 13 }} />
                              <LatexHelper isZh={isZh} onInsert={s => insertParamUnit(idx, s)} />
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                              {COMMON_UNITS.map(u => (
                                <span key={u.label} onClick={() => updateParam(idx, { unit: u.latex })}
                                  style={{ cursor: 'pointer', fontSize: 10, padding: '2px 6px', background: '#e8f4e8', borderRadius: 10, color: '#2d6a2d', border: '1px solid #b7ddb7' }}>
                                  {u.label}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* 第二行：中文名稱 + 英文名稱 */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 8 }}>
                          <div>
                            <label style={{ ...paramLabelStyle }}>{isZh ? '中文名稱' : 'Chinese Name'}</label>
                            <input value={p.name_zh} onChange={e => updateParam(idx, { name_zh: e.target.value })}
                              style={{ ...cellInput, width: '100%' }} />
                          </div>
                          <div>
                            <label style={{ ...paramLabelStyle }}>{isZh ? '英文名稱' : 'English Name'}</label>
                            <input value={p.name_en} onChange={e => updateParam(idx, { name_en: e.target.value })}
                              style={{ ...cellInput, width: '100%' }} />
                          </div>
                        </div>

                        {/* 第三行：中文說明 + 英文說明 */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                          <div>
                            <label style={{ ...paramLabelStyle }}>{isZh ? '中文說明' : 'Chinese Description'}</label>
                            <input value={p.description_zh || ''} onChange={e => updateParam(idx, { description_zh: e.target.value })}
                              style={{ ...cellInput, width: '100%' }} />
                          </div>
                          <div>
                            <label style={{ ...paramLabelStyle }}>{isZh ? '英文說明' : 'English Description'}</label>
                            <input value={p.description_en || ''} onChange={e => updateParam(idx, { description_en: e.target.value })}
                              style={{ ...cellInput, width: '100%' }} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 區塊四：說明備註 */}
              <div style={{ background: '#fafafa', borderRadius: 8, padding: '16px 20px', border: '1px solid #eee' }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#333', marginBottom: 12 }}>
                  {isZh ? '說明備註' : 'Description / Notes'}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={labelStyle}>{isZh ? '中文說明' : 'Chinese Description'}</label>
                    <textarea value={form.description_zh || ''} onChange={e => setFormField('description_zh', e.target.value)}
                      rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
                  </div>
                  <div>
                    <label style={labelStyle}>{isZh ? '英文說明' : 'English Description'}</label>
                    <textarea value={form.description_en || ''} onChange={e => setFormField('description_en', e.target.value)}
                      rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>{t('common.cancel')}</button>
              <button className="btn-primary" onClick={handleSave} disabled={loading}>
                {loading ? (isZh ? '儲存中...' : 'Saving...') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── 試算 Modal ────────────────────────────────────────────── */}
      {showTrial && trialFormula && (
        <div className="modal-overlay" onClick={() => setShowTrial(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 640, width: '95vw' }}>
            <div className="modal-header">
              <h2>
                {isZh ? '試算' : 'Trial Calculation'} — {trialFormula.formula_code}
                <span style={{ fontWeight: 400, fontSize: 14, marginLeft: 8, color: 'rgba(255,255,255,0.75)' }}>
                  {isZh ? trialFormula.name_zh : trialFormula.name_en}
                </span>
              </h2>
              <button className="modal-close" onClick={() => setShowTrial(false)}>✕</button>
            </div>

            <div className="modal-body">
              {/* 公式預覽 */}
              {trialFormula.formula_latex && (
                <div style={{ padding: '12px 16px', background: '#f8f8f8', borderRadius: 8, marginBottom: 16, overflowX: 'auto', border: '1px solid #eee' }}>
                  <SafeBlockMath math={trialFormula.formula_latex} />
                </div>
              )}

              {/* 參數輸入（僅顯示等號右邊的參數） */}
              {(() => {
                const lhsSym = getLhsSymbol(trialFormula.formula_latex || '');
                const lhsParam = trialFormula.parameters.find(p => p.symbol === lhsSym);
                const rhsParams = trialFormula.parameters.filter(p => p.symbol !== lhsSym);

                return (
                  <>
                    {rhsParams.length === 0 ? (
                      <div style={{ color: '#999', textAlign: 'center', padding: '16px 0' }}>
                        {isZh ? '此公式無定義參數' : 'No parameters defined for this formula'}
                      </div>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16 }}>
                        <thead>
                          <tr style={{ background: '#f0f7f0' }}>
                            <th style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600 }}>{isZh ? '符號' : 'Symbol'}</th>
                            <th style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600 }}>{isZh ? '名稱' : 'Name'}</th>
                            <th style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600 }}>{isZh ? '單位' : 'Unit'}</th>
                            <th style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600 }}>{isZh ? '試算值' : 'Trial Value'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rhsParams.map((p, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                              <td style={{ padding: '6px 10px' }}>
                                {p.symbol ? <SafeInlineMath math={p.symbol} /> : '—'}
                              </td>
                              <td style={{ padding: '6px 10px', color: '#555' }}>
                                {isZh ? (p.name_zh || p.name_en || '—') : (p.name_en || p.name_zh || '—')}
                              </td>
                              <td style={{ padding: '6px 10px', color: '#666', fontSize: 12 }}>
                                {p.unit ? <SafeInlineMath math={p.unit} /> : '—'}
                              </td>
                              <td style={{ padding: '6px 10px' }}>
                                <input
                                  type="number"
                                  value={trialValues[p.symbol] ?? ''}
                                  onChange={e => {
                                    const val = e.target.value;
                                    setTrialValues(prev => ({ ...prev, [p.symbol]: val }));
                                    setTrialResult('pending');
                                  }}
                                  style={{ width: 120, padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: 5, fontSize: 13 }}
                                  placeholder="0"
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    {/* 計算結果 */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                      <button className="btn-primary" onClick={handleCalculate} style={{ padding: '7px 20px' }}>
                        ▶ {isZh ? '計算' : 'Calculate'}
                      </button>
                      <div style={{ flex: 1 }}>
                        {trialResult === 'pending' && (
                          <div style={{ color: '#aaa', fontSize: 13, fontStyle: 'italic' }}>
                            {isZh ? '點擊「計算」進行試算' : 'Click "Calculate" to evaluate'}
                          </div>
                        )}
                        {trialResult === 'error' && (
                          <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, padding: '8px 12px', color: '#7d5a00', fontSize: 13 }}>
                            {isZh
                              ? '⚠ 無法自動計算，請確認所有參數已填入數值，且公式不含 Σ 等聚合符號'
                              : '⚠ Cannot calculate. Ensure all parameters are filled and formula has no aggregation operators (Σ etc.)'}
                          </div>
                        )}
                        {typeof trialResult === 'number' && (
                          <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, padding: '10px 16px', display: 'flex', alignItems: 'baseline', gap: 8 }}>
                            {lhsSym && (
                              <span style={{ fontSize: 15, color: '#444' }}>
                                <SafeInlineMath math={lhsSym} /> =
                              </span>
                            )}
                            <span style={{ fontSize: 20, fontWeight: 700, color: '#1a5f1a' }}>
                              {trialResult.toLocaleString()}
                            </span>
                            {lhsParam?.unit && (
                              <span style={{ fontSize: 12, color: '#555' }}>
                                <SafeInlineMath math={lhsParam.unit} />
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowTrial(false)}>{t('common.close') || (isZh ? '關閉' : 'Close')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── 共用樣式 ──────────────────────────────────────────────────────
const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 13, color: '#444',
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '6px 10px', border: '1px solid #d9d9d9',
  borderRadius: 6, fontSize: 13, boxSizing: 'border-box',
};
const cellInput: React.CSSProperties = {
  padding: '4px 6px', border: '1px solid #d9d9d9', borderRadius: 4,
  fontSize: 12, boxSizing: 'border-box',
};
const paramLabelStyle: React.CSSProperties = {
  display: 'block', marginBottom: 3, fontWeight: 500, fontSize: 11, color: '#888',
};

export default FormulaConfigPage;
